import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CreateSupplierRequest, SupplierDto } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import PartyStatement from './PartyStatement';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  canCreate: boolean;
  /** يُفتح كشفه مباشرة — من أعمار الموردين مثلاً. */
  initialSupplierId?: string | null;
  onSessionLost: () => void;
}

const EMPTY = {
  nameAr: '',
  taxRegistrationNumber: '',
  phone: '',
  email: '',
  paymentTermsDays: '0',
};

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const paths = caught.problem.issues?.map((issue) => issue.path ?? issue.message) ?? [];
    return (
      (caught.problem.detail ?? caught.problem.title) +
      (paths.length ? ': ' + paths.join('، ') : '')
    );
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/** الموردون: قائمة وإضافة وكشف — مرآة شاشة العملاء. الرصيد من الدفتر لا من المورد. */
export default function Suppliers({
  companyId,
  currency,
  canCreate,
  initialSupplierId = null,
  onSessionLost,
}: Props) {
  const [statementOf, setStatementOf] = useState<string | null>(initialSupplierId);
  const [suppliers, setSuppliers] = useState<SupplierDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(EMPTY);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .suppliers(companyId)
      .then(setSuppliers)
      .catch((caught: unknown) => {
        if (caught instanceof UnauthorizedError) onSessionLost();
        else setError(messageOf(caught));
      });
  }, [companyId, onSessionLost]);

  useEffect(load, [load]);

  function update(key: keyof typeof EMPTY, value: string): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setIdempotencyKey(crypto.randomUUID());
    setSaved(null);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const body: CreateSupplierRequest = {
      nameAr: draft.nameAr,
      taxRegistrationNumber: draft.taxRegistrationNumber,
      phone: draft.phone,
      email: draft.email,
      paymentTermsDays: Number(draft.paymentTermsDays),
    };
    try {
      const created = await api.createSupplier(companyId, idempotencyKey, body);
      setSaved(created.code + ' — ' + created.nameAr);
      setDraft(EMPTY);
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {canCreate && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>مورد جديد</h2>
          <div className="form-row">
            <label className="grow">
              الاسم
              <input
                value={draft.nameAr}
                onChange={(event) => update('nameAr', event.target.value)}
                maxLength={200}
              />
            </label>
            <label>
              التسجيل الضريبى
              <input
                value={draft.taxRegistrationNumber}
                onChange={(event) => update('taxRegistrationNumber', event.target.value)}
                inputMode="numeric"
                maxLength={32}
              />
              <Hint>رقم البطاقة الضريبية للمورد (٩ أرقام)، كما على فواتيره.</Hint>
            </label>
          </div>
          <div className="form-row">
            <label>
              الهاتف
              <input
                value={draft.phone}
                onChange={(event) => update('phone', event.target.value)}
                inputMode="tel"
                maxLength={32}
              />
            </label>
            <label className="grow">
              البريد
              <input
                type="email"
                value={draft.email}
                onChange={(event) => update('email', event.target.value)}
                maxLength={254}
              />
            </label>
            <label>
              مدة السداد (يوم)
              <input
                type="number"
                min={0}
                max={365}
                value={draft.paymentTermsDays}
                onChange={(event) => update('paymentTermsDays', event.target.value)}
              />
              <Hint>كم يومًا يمهلك المورد للدفع، فيذكّرك البرنامج بموعد السداد.</Hint>
            </label>
          </div>
          {error && <p className="error">{error}</p>}
          {saved && <p className="sub">تم الحفظ: {saved}</p>}
          <div className="form-actions">
            <button
              type="submit"
              className="primary"
              disabled={saving || draft.nameAr.trim().length < 2}
            >
              {saving ? 'جارى الحفظ…' : 'حفظ المورد'}
            </button>
          </div>
        </form>
      )}

      <div className="panel">
        <h2>الموردون</h2>
        {!canCreate && error && <p className="error">{error}</p>}
        {suppliers === null && !error && <Loading />}
        {suppliers?.length === 0 && <p className="sub">لا موردين بعد.</p>}
        {suppliers && suppliers.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>الاسم</th>
                  <th>التسجيل الضريبى</th>
                  <th>الهاتف</th>
                  <th>مدة السداد</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <tr
                    key={supplier.id}
                    className="clickable"
                    tabIndex={0}
                    aria-selected={statementOf === supplier.id}
                    onClick={() => setStatementOf(supplier.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') setStatementOf(supplier.id);
                    }}
                  >
                    <td className="mono">{supplier.code}</td>
                    <td>{supplier.nameAr}</td>
                    <td className="mono">{supplier.taxRegistrationNumber ?? ''}</td>
                    <td className="mono">{supplier.phone ?? ''}</td>
                    <td>{supplier.paymentTermsDays} يوم</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {suppliers && suppliers.length > 0 && <p className="label">اضغط أى مورد لفتح كشف حسابه.</p>}
      </div>

      {statementOf && (
        <PartyStatement
          key={statementOf}
          companyId={companyId}
          kind="supplier"
          partyId={statementOf}
          currency={currency}
          onSessionLost={onSessionLost}
        />
      )}
    </>
  );
}
